// File: src/components/potus/TruthSocialCard.jsx
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { parsePostDate } from '../../utils/constants';
import CloseIcon from '@mui/icons-material/Close';
// Import the Trump image
import trumpImage from '../../images/potus/realDonaldTrump.jpeg';
import truthLogo from '../../images/potus/Tlogo.png';

const TruthSocialCard = ({ post }) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const formatPostDate = (dateString) => {
    try {
      const date = parsePostDate(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <Card
        variant="stats"
        sx={{
          mb: 2,
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(19, 47, 76, 0.4)'  // Same as Executive Orders
            : 'rgba(255, 255, 255, 0.8)',  // Same as Executive Orders
          backdropFilter: 'blur(10px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          }
        }}
      >
        <CardContent>
          {/* Header with Avatar and Truth Social branding */}
          <Box display="flex" alignItems="flex-start" mb={2}>
            <Avatar
              src={trumpImage}
              alt="Donald Trump"
              sx={{
                width: 40,
                height: 40,
                mr: 2
              }}
            >
              DT
            </Avatar>

            <Box flexGrow={1}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <Typography variant="h6" component="h3">
                  Donald J. Trump
                </Typography>

                {/* Truth Social Logo - Clean Implementation */}
                <Box
                  component="img"
                  src={truthLogo}
                  alt="Truth Social"
                  sx={{
                    ml: 1,
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    objectFit: 'contain', // Ensures the logo fits properly
                    backgroundColor: 'transparent', // Remove background if logo has transparency
                    // Fallback styling if image fails to load
                    '&:error': {
                      display: 'none'
                    }
                  }}
                  onError={(e) => {
                    // Fallback to text "T" if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />

                {/* Fallback T logo if image fails */}
                <Box
                  sx={{
                    ml: 1,
                    width: 20,
                    height: 20,
                    backgroundColor: '#5448ee',
                    borderRadius: '4px',
                    display: 'none', // Hidden by default, shown if image fails
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}
                  >
                    T
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary">
                @realDonaldTrump
              </Typography>
            </Box>
          </Box>

          {/* Content Preview */}
          <Typography variant="body2" color="text.primary" paragraph>
            {post.content?.length > 280
              ? post.content.substring(0, 280) + '...'
              : post.content
            }
          </Typography>

          {/* Footer - UPDATED */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderTop={1}
            borderColor="divider"
            pt={2}
          >
            <Typography variant="caption" color="text.secondary">
              {formatPostDate(post.date)}
            </Typography>

            <Box display="flex" gap={1} alignItems="center">
              {/* Truth Social Logo Button - NEW */}
              {post.original_url && (
                <IconButton
                  component={Link}
                  href={post.original_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.2)' 
                        : 'rgba(0, 0, 0, 0.08)',
                    },
                    p: 1
                  }}
                  aria-label="View original post on Truth Social"
                >
                  <Box
                    component="img"
                    src={truthLogo}
                    alt="Truth Social"
                    sx={{
                      width: 16,
                      height: 16,
                      objectFit: 'contain'
                    }}
                  />
                </IconButton>
              )}
              
              {/* Read More Button */}
              {post.content?.length > 280 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleOpenModal}
                >
                  Read More
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Modal for full content */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(13, 34, 55, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar
                src={trumpImage}
                alt="Donald Trump"
                sx={{
                  width: 32,
                  height: 32,
                  mr: 2
                }}
              >
                DT
              </Avatar>
              <Box>
                <Typography variant="h6">Donald J. Trump</Typography>
                <Typography variant="body2" color="text.secondary">
                  @realDonaldTrump
                </Typography>
              </Box>
            </Box>

            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" paragraph>
            {post.content}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {formatPostDate(post.date)}
          </Typography>
        </DialogContent>

        <DialogActions>
          {/* Truth Social Logo Button in Modal - NEW */}
          {post.original_url && (
            <IconButton
              component={Link}
              href={post.original_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.2)' 
                    : 'rgba(0, 0, 0, 0.08)',
                },
                mr: 1
              }}
              aria-label="View original post on Truth Social"
            >
              <Box
                component="img"
                src={truthLogo}
                alt="Truth Social"
                sx={{
                  width: 20,
                  height: 20,
                  objectFit: 'contain'
                }}
              />
            </IconButton>
          )}
          <Button onClick={handleCloseModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TruthSocialCard;